import { ref } from "vue";
import { classifyApiError, type ApiError } from "./useApiError";

/**
 * The loading/error bookkeeping every detail view needs, in one place: load the resource, and
 * on failure keep the classified error so the view can render `ResourceErrorState` in place of
 * its content. Views used to swallow this and sit on "Loading..." forever.
 */
export function useResourceState() {
  const loading = ref(true);
  const error = ref<ApiError | null>(null);

  /** Runs `load`, capturing any failure as a classified error rather than letting it escape. */
  const runLoad = async (
    load: () => Promise<void>,
    options: { silent?: boolean } = {}
  ): Promise<void> => {
    if (!options.silent) {
      loading.value = true;
    }
    error.value = null;
    try {
      await load();
    } catch (thrown) {
      const classified = classifyApiError(thrown);
      // A 401 is already being answered by the interceptor's redirect to login. Rendering an
      // error here as well would only flash behind that navigation.
      if (classified.kind !== "unauthenticated") {
        error.value = classified;
      }
      console.error("Failed to load resource", thrown);
    } finally {
      loading.value = false;
    }
  };

  /**
   * For reloading after the user did something on the page — accepted an offer, changed a
   * status. Leaves the content on screen instead of replacing it with the loading state, which
   * would make every in-place action flash the whole view.
   */
  const runRefresh = (load: () => Promise<void>): Promise<void> =>
    runLoad(load, { silent: true });

  return { loading, error, runLoad, runRefresh };
}
